// Species definitions. A species fixes what an individual eats, who eats it,
// and the RANGE each of its genes may span; the individual's genome picks a
// point inside those ranges and varies everything else (including its brain).
//
// ---------------------------------------------------------------------------
// DATASHEET CONVENTION — read this before adding a species
// ---------------------------------------------------------------------------
// Every range below is written in real units through the helpers in scale.ts
// (kg / kmh / metres / years), with the field figure and its source in a comment
// beside it. A gene range is meant to span the plausible variation among adults
// of that species, not a single textbook average — evolution needs somewhere to
// move. Where a value is a deliberate compression rather than a real figure it
// says so and explains why; where a value is simply tuned with no field source,
// it says THAT too, so nothing pretends to an authority it doesn't have.
//
// Adding a species (squirrel, bear, …): copy this shape, research the same ten
// traits, cite them, and wire the prey/predator ids.
//
// AND ANSWER `breedersPerGroup`. Every species carries it, and the honest answer
// for most is `Infinity` (no reproductive suppression). But it is the only thing
// in the model that limits a population from above rather than by starving it,
// so getting it wrong does not look like a bug — it looks like a species that
// booms and takes the ecosystem down with it, or one that quietly needs a rescue
// floor forever. The wolves cost this project a long time before it existed.
// ---------------------------------------------------------------------------

import type { TraitRanges } from "./traits";
import { kg, kmh, metres, years } from "./scale";

export type Diet = "herbivore" | "carnivore";

export interface Species {
  id: number;
  key: string;
  name: string;
  /** Binomial name, so the datasheet is checkable against the literature. */
  binomial: string;
  diet: Diet;
  color: string;
  preyOf: number[]; // species ids that hunt this one
  huntsSpecies: number[]; // species ids this one eats (carnivores)
  /**
   * Contact distance for reproduction, in metres. Species-specific because
   * social structure is: wolves live and travel as a pack, so a pack-mate is
   * effectively always to hand, while deer meet as individuals during the rut.
   */
  mateDist: number;

  // --- Sociality (Reynolds cohesion/separation, NoC Ch.5) -------------------
  /** Steering weight pulling an animal toward nearby members of its own species. */
  cohesion: number;
  /** How far it looks for company, in metres. */
  cohesionRadius: number;
  /** Preferred spacing: closer than this and it steers apart instead. */
  spacing: number;

  // --- Cooperative breeding / reproductive suppression ----------------------
  //
  // EVERY species must answer this, including future ones. It is not a wolf
  // patch: it is the one mechanism in the model that limits a population from
  // ABOVE rather than by starving it, and a species without an honest answer
  // here will either explode or need a rescue floor to exist.
  //
  // Measured, on the wolves: with unlimited breeding, one 400-1,360 energy
  // carcass funds up to eight pups at a gestation cost of 100-450, so the pack
  // converts a good hunting streak straight into wolves. In an 80k-tick closed
  // run they peaked at 658, stripped the herd from 2,601 to 2, and starved. No
  // grass setting avoids that, because grass only regulates from below.

  /**
   * How many females in one social group may breed at the same time.
   *
   * `Infinity` — no suppression: every mature female breeds on her own account.
   * This is the normal case (deer and most ungulates, rodents, bears).
   *
   * A small integer — COOPERATIVE BREEDING: a dominant female monopolises
   * reproduction and subordinates are reproductively suppressed. Gray wolves,
   * African wild dogs, meerkats, dwarf mongooses and naked mole-rats all do
   * this, and in wolves it is close to absolute — a pack of ten normally
   * produces exactly one litter a year, from the breeding female.
   *
   * The population consequence is the point: pack output stops scaling with
   * pack size, so a species cannot answer abundance by breeding faster. It can
   * only answer it by founding more groups, which needs ground to put them on —
   * and that is what turns the cap into a genuine density-dependent brake.
   */
  breedersPerGroup: number;

  /**
   * Radius in metres within which breeding rank is contested — the social
   * group's effective territory. Ignored when `breedersPerGroup` is Infinity.
   *
   * Larger than `cohesionRadius` on purpose: a pack defends far more ground
   * than it huddles within, and it is the DEFENDED area that decides how many
   * breeding groups the map can hold.
   *
   * THIS NUMBER SETS THE PREDATOR:PREY RATIO. Wolf abundance tracks the count of
   * territories the map can hold, not prey availability — they are capped by
   * breeding, not by food. Measured on the 17.3 km² default, closed, 3 seeds:
   *
   *   radius   packs   persist   deer    wolf   ratio    wolfCV
   *    300 m    61.1     3/3     4177     678   1:6       0.35   ← current
   *    800 m     8.6     3/3    10202     142   1:72      0.15
   *   1600 m     2.1     3/3     5071      35   1:145     0.35
   *   2400 m     1.0     1/3     7698      36   1:214     0.12
   *
   * 800 m reaches a real ratio (life runs 1:100-500) AND has the lowest wolf
   * volatility in the table. Beyond that the map holds one or two packs and a
   * single bad season takes the predators out — which is honest fragility rather
   * than a bug, but not a good default.
   *
   * At 300 m a territory is 0.28 km² against a real gray wolf's 100-1,000 km².
   * That is the number that was wrong by three orders of magnitude, and it is
   * the same constraint as everywhere else in this project: real wolf spacing is
   * larger than the whole map. Not yet changed pending an 80k+ confirmation —
   * 50k has misled this project more than once.
   */
  breedingGroupRadius: number;

  /**
   * Offspring per successful breeding, [min, max] inclusive.
   *
   * The other half of `breedersPerGroup`, and meaningless without it. A cap on
   * BREEDERS sets how many litters a group produces; this sets how big a litter
   * is, and only the product of the two is the population's actual output.
   * Capping wolves to one breeding female while every birth produced a single
   * pup cut a pack to a fifth of its real fecundity and drove them extinct on
   * 3/3 seeds — the opposite failure to leaving them uncapped.
   *
   * Parental investment is CONSERVED across the litter: a mother pays
   * `gestationCost × maxEnergy` once and it is divided among the offspring, so
   * a large litter means small, fragile young rather than free biomass. For a
   * species whose young cannot fend for themselves at that size, pair this with
   * `sharesFood` so the group can carry them.
   */
  litterSize: readonly [number, number];

  /**
   * Does a successful hunter divide its kill with the group, rather than eating
   * alone?
   *
   * True for wolves and other cooperative hunters: a carcass is shared at the
   * kill site and pups are fed first, which is what makes a litter of helpless
   * young survivable at all. Irrelevant for grazers, whose food is not a
   * discrete windfall — set false.
   */
  sharesFood: boolean;

  // --- Activity rhythm ------------------------------------------------------
  //
  // Also required of every species. Animals do not move continuously; they run
  // a daily cycle of feeding, travelling and lying up, in BOUTS that persist.
  // Before these existed every agent re-decided its behaviour from scratch every
  // tick, which is why the herd drifted like a fluid instead of behaving.

  /**
   * How strongly this species' activity is tied to dawn and dusk.
   * 0 = active around the clock (cathemeral); 1 = strongly crepuscular.
   *
   * White-tailed deer are textbook crepuscular — feeding peaks sharply at dawn
   * and dusk, with the animal bedded and ruminating through midday and the small
   * hours. Wolves are more flexible, hunting whenever prey is catchable, but
   * still favour the cooler ends of the day.
   *
   * This only sets a BIAS on the rest drive. The brain gets the time of day as
   * an input and can learn to override it, which is the point — an animal that
   * discovers a better schedule should be free to keep it.
   */
  activityRhythm: number;

  /**
   * How long one behavioural bout lasts, [min, max] in sim-seconds. Persistence
   * is what makes movement read as behaviour: a deer grazes a patch for a while,
   * then beds down for a while. Re-deciding every tick produces neither.
   */
  boutSeconds: readonly [number, number];

  /**
   * Sim-seconds an adult will tolerate being reproductively SUPPRESSED before it
   * leaves to find territory of its own. `Infinity` = never disperses.
   *
   * This is the other half of `breedersPerGroup`, and suppression without it is
   * a modelling error rather than a simplification. In a real pack the thing
   * that caps group size is that subordinates LEAVE — a wolf that cannot breed
   * at home disperses at one to three years old to find vacant ground and an
   * unrelated mate. Suppress reproduction but give a subordinate no reason to
   * go, and it simply stays, keeps eating shared kills, and the group
   * accumulates: measured here as packs of 40+, peaking at 88, against a real
   * gray wolf average near 8 and a largest-ever recorded wild pack of 37.
   *
   * Dispersal pays for itself without needing a cost constant. A disperser
   * leaves the pack, so it loses pack hunting support and its share of every
   * kill until it settles — which is exactly why real dispersal carries high
   * mortality.
   *
   * IT NEEDS ROOM, AND IT IS NOT FREE ON A SMALL MAP. Measured A/B on the
   * shipped 1.9 km² map, same seeds, floors on, 50k ticks:
   *
   *   dispersal OFF   6/6   CV 0.45/0.55   deer 339
   *   dispersal ON    6/6   CV 0.79/0.83   deer  69
   *
   * Volatility nearly doubles and the herd falls five-fold, with wolf numbers
   * unchanged. A dispersal run covers a large fraction of a 1.6 km world, so it
   * spreads predators evenly across the entire map and destroys the prey refuge
   * that spatial structure would otherwise provide. The same behaviour on a map
   * where a dispersal run is a small fraction of the world founds new
   * territories instead of blanketing the existing ones.
   */
  dispersalDelay: number;


  traits: TraitRanges;
}

/**
 * WHITE-TAILED DEER — Odocoileus virginianus
 *
 * Mass       males 68–136 kg, females 40–92 kg      (a-z-animals.com)
 * Speed      ~40 km/h sustained, 48–64 km/h burst   (a-z-animals.com, NJDEP)
 * Lifespan   2–6 typical in the wild, 20+ maximum   (NJDEP, Cosley Zoo)
 * Maturity   usually first breeds at 1.5 y, some at 6–7 months (Maryland DNR)
 * Intake     ~2% of body mass/day to maintain, 3–5% to reproduce (Texas Land Assoc.)
 * Metabolism maintenance scales as W^0.75 — Kleiber   (Can. J. Zool. 1995)
 */
const DEER_TRAITS: TraitRanges = {
  mass: [kg(40), kg(136)], // full adult range across both sexes
  maxSpeed: [kmh(40), kmh(64)], // sustained run → burst sprint
  maxForce: [60, 300], // TUNED — agility proxy, no field source
  senseRadius: [metres(80), metres(350)], // COMPRESSED: real alert distance is
  // context-dependent and often longer, but a deer that senses a large share of
  // a map this size leaves predators no cover to work with.
  metabolicRate: [0.7, 1.4], // TUNED — individual variation about the Kleiber baseline
  maxEnergy: [120, 350], // TUNED — fat reserves PER UNIT BODY SCALE; the actual
  // tank is this × size, so a heavy deer really does carry more (deer fatten hard
  // for winter, and it is the big ones that survive it)
  reproThreshold: [0.5, 0.85], // TUNED — condition needed to breed
  gestationCost: [0.2, 0.45], // TUNED — ~200 d gestation, 1–2 fawns; litter size not modelled
  maturityAge: [years(0.6), years(2)], // 6–7 months at the early extreme, 1.5 y typical
  lifespan: [years(2), years(14)], // wild-typical floor, long-lived tail
  // DELIBERATELY THE SAME RANGE AS THE WOLF. Signalling is a capability every
  // animal has; where it lands is for selection to decide, not for this file.
  alarmSignal: [0, metres(220)],
};

/**
 * GRAY WOLF — Canis lupus
 *
 * Mass       14–65 kg depending on region, ~45 kg average  (Britannica, ADW)
 * Speed      56–72 km/h sprint (35–45 mph), 8–10 km/h trot (Biology Insights)
 * Lifespan   6–8 typical in the wild, ~13 maximum          (Wildlife Science Center)
 * Maturity   disperses to breed after 2+ years             (NWF)
 * Senses     smells prey to ~2.5 km, sees movement to ~1.5 km (Animals Around the Globe)
 * Hunting    80–90% of attacks on wild ungulates FAIL; ~20% success on
 *            white-tailed deer in Minnesota, 14% mean across 14 studies
 *            (Wolves on the Hunt via NPS; Discover Wildlife)
 *
 * Note the speed figures: a wolf is barely faster than a deer, so it cannot
 * simply outrun healthy prey. It wins on endurance and on picking animals that
 * are already failing — which is why hunting in world.ts is a condition-weighted
 * roll rather than a guaranteed kill.
 */
const WOLF_TRAITS: TraitRanges = {
  mass: [kg(20), kg(65)], // adult range; the 14 kg extreme is a small southern subspecies
  maxSpeed: [kmh(56), kmh(72)], // sprint only — sustaining it is priced by moveCost ∝ v²
  maxForce: [70, 320], // TUNED — agility proxy, no field source
  senseRadius: [metres(200), metres(700)], // COMPRESSED from a real ~2.5 km scent range,
  // which would cover the entire map and erase every refuge. The wolf > deer
  // ordering is the part that's real and is preserved.
  metabolicRate: [0.7, 1.4], // TUNED
  maxEnergy: [500, 1000], // wolves gorge — up to ~20% of body weight at a sitting —
  // and cache the remainder. PER UNIT BODY SCALE: the tank is this × size.
  // A pack no longer needs one wolf to swallow a whole carcass, because
  // `sharesFood` spreads a kill across the animals at it; before sharing existed
  // this range had to be large enough to absorb an entire deer or the surplus was
  // silently discarded.
  reproThreshold: [0.3, 0.6], // wolves breed once a year in seasonal synchrony rather
  // than only at peak condition, so the bar is lower than a deer's. Holding them
  // to a deer's threshold meant a sparse pack met, was never simultaneously fat,
  // and never bred at all.
  gestationCost: [0.2, 0.45], // TUNED — ~63 d gestation, 4–6 pups; litter size not modelled
  maturityAge: [years(1.5), years(3)], // disperses and breeds at ~2 y
  lifespan: [years(3), years(13)], // 6–8 typical, ~13 maximum
  alarmSignal: [0, metres(220)], // identical to the deer's, on purpose — see above
};

export const DEER: Species = {
  id: 0,
  key: "deer",
  name: "Deer",
  binomial: "Odocoileus virginianus",
  diet: "herbivore",
  color: "#c9a36a",
  preyOf: [1],
  huntsSpecies: [],
  mateDist: 15,
  cohesion: 0.22, // deer band loosely into family groups — loosely, because a hard
  // pull keeps them jostling and a jostling deer is one that never gets to feed
  cohesionRadius: 120,
  spacing: 14,
  breedersPerGroup: Infinity, // no suppression: every doe that can carry a fawn does.
  // White-tailed deer are polygynous and non-territorial in the breeding sense —
  // bucks compete, does do not, and a family group is not a breeding unit.
  breedingGroupRadius: 0, // unused while breedersPerGroup is Infinity
  litterSize: [1, 2], // 1 fawn for a first-time doe, twins routine thereafter,
  // triplets on good range (Maryland DNR, Texas Land Assoc.)
  sharesFood: false, // grass is not a windfall to divide
  activityRhythm: 0.85, // strongly crepuscular; feeding peaks at dawn and dusk and
  // the animal beds down to ruminate through midday (NJDEP, Maryland DNR)
  boutSeconds: [8, 30], // a deer feeds in several discrete bouts per day, bedding
  // between them to chew cud — the bout, not the tick, is its unit of behaviour
  dispersalDelay: Infinity, // no reproductive suppression to escape, so nothing to
  // disperse FROM. Yearling bucks do leave their natal range in life, but that is
  // inbreeding avoidance rather than the group-size regulator modelled here.
  traits: DEER_TRAITS,
};

export const WOLF: Species = {
  id: 1,
  key: "wolf",
  name: "Wolf",
  binomial: "Canis lupus",
  diet: "carnivore",
  color: "#8fa0b0",
  preyOf: [],
  huntsSpecies: [0],
  mateDist: 60, // a pack travels together; pair-bonding doesn't need a chance collision
  cohesion: 0.7, // a pack moves as one unit — this is the whole basis of wolf hunting
  cohesionRadius: 220,
  spacing: 25,
  breedersPerGroup: 1, // one litter per pack per season. A gray wolf pack contains
  // several sexually mature females, but the breeding female suppresses the rest;
  // multiple litters occur only in unusually large or food-rich packs (Mech &
  // Boitani, Wolves: Behavior, Ecology and Conservation). Modelled as absolute.
  breedingGroupRadius: 300, // TUNED, not a field figure. Real gray wolf territories
  // run 100-1,000 km², still far larger than the whole 17.3 km² map; compressed to a
  // radius that lets a few packs coexist here. The ratio to cohesionRadius — a pack
  // defends much more ground than it huddles within — is the part that's real.
  litterSize: [4, 6], // gray wolf litters average 4-6 pups (NWF, ADW)
  sharesFood: true, // the pack eats at the carcass together and pups are fed first;
  // without this a litter of four cannot survive its own share of one gestation
  activityRhythm: 0.6, // less tied to dawn/dusk than deer — wolves hunt when prey is
  // catchable — but they still rest 8-14 h a day rather than roaming continuously
  boutSeconds: [12, 45], // long travel bouts, long rests; a pack covers ground in
  // purposeful stints and then lies up together
  dispersalDelay: 55, // ~0.6 sim-years of being an adult who cannot breed. Real
  // wolves disperse at 1-3 years old, i.e. within a year or two of maturing into a
  // pack that already has a breeding pair.
  traits: WOLF_TRAITS,
};

export const SPECIES: Species[] = [DEER, WOLF];

export function speciesById(id: number): Species {
  return SPECIES[id];
}
